import { useAuth } from "react-oidc-context";
import { ArrowUpRight, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { Logo } from "@/components/layout/Logo";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getFirstName } from "@/lib/userDisplay";
import { cn } from "@/lib/utils";
import { SECTION_DEFINITIONS, type SectionId } from "@/config/uiConfig";
import { getNavGroupEntryHref, useAuthorizedNavGroups } from "@/components/layout/NavSidebar";
import { useAppContext } from "@/contexts/AppContext";
import { useAccount } from "@/hooks/useAccount";
import { useAccountSetup } from "@/hooks/useAccountSetup";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import { usePinnedDashboards } from "@/hooks/dashboard/usePinnedDashboards";
import { useTenants } from "@/hooks/useTenants";
import {
  balancedGridClass,
  CountBadge,
  DashboardCard,
  HomeDashboardsSkeleton,
  HomeScope,
  HomeSection,
  RecentLinks,
  SectionButton,
  SpaceLinks,
  SpacePanel,
  ViewAllTile,
} from "./HomeSections";
import { HomeAccessSetup } from "./HomeAccessSetup";
import { describePerimeter, selectHomeDashboards } from "./homeDashboards";

const DASHBOARD_LIST_HREF = "/workspace/dashboards";

// ─── Exported helpers (used by tests and nav) ─────────────────────────────────

export function getAuthorizedHomeSections(canAccessSection: (sectionId: SectionId) => boolean) {
  return SECTION_DEFINITIONS.filter((section) => canAccessSection(section.id));
}

function SectionTile({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-xl border border-border bg-surface p-4 transition-colors hover:border-brand-cyan/50 hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <div className="flex items-center justify-between">
        <Icon className="size-5 text-brand-cyan" aria-hidden="true" />
        <ArrowUpRight
          className="size-4 text-muted group-hover:text-brand-cyan"
          aria-hidden="true"
        />
      </div>
      <h3 className="mt-4 text-base font-medium text-primary">{title}</h3>
      <p className="mt-1.5 flex-1 text-xs leading-5 text-secondary">{description}</p>
    </Link>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HomePage() {
  useDocumentTitle("Home");
  const auth = useAuth();
  const { canAccessSection, profile } = useAuthorization();
  const account = useAccount();
  const tenants = useTenants();
  const { needsSetup } = useAccountSetup();
  const { recents } = useAppContext();
  const hasWorkspace = canAccessSection("workspace");
  const dashboardsQuery = useDashboards({ relationship: "accessible", state: "saved" });
  const navGroups = useAuthorizedNavGroups();
  const username = getFirstName(auth.user?.profile);
  const pins = usePinnedDashboards();
  const home = selectHomeDashboards(dashboardsQuery.data ?? [], recents, pins.ids);
  const pinnedIds = new Set(home.pinned.map((dashboard) => dashboard.id));

  const sections = navGroups
    .filter((group) => group.id !== "home" && canAccessSection(group.id as SectionId))
    .map((group) => ({
      id: group.id,
      href: getNavGroupEntryHref(group),
      icon: group.icon,
      label: group.label,
      description: SECTION_DEFINITIONS.find((s) => s.id === group.id)?.description ?? "",
    }));

  const showDashboards = hasWorkspace && dashboardsQuery.isSuccess && home.total > 0;
  const perimeter = describePerimeter(
    account.data?.scopes ?? [],
    tenants.data ?? [],
    account.data?.data_limited_to_scopes ?? false,
  );

  const dashboardsPending = hasWorkspace && dashboardsQuery.isPending;
  const noDashboards = hasWorkspace && dashboardsQuery.isSuccess && home.total === 0;

  // The dashboards lead Home for every profile: they are what people come for. The
  // spaces follow as one compact row; without dashboards (none yet, or no workspace),
  // the spaces are the page and keep their full tiles. A lone space is shown only
  // once the dashboards are known: when it is the Workspace and dashboards exist,
  // they replace it.
  const loneSpace = sections.length === 1 ? sections[0] : undefined;
  const showSpaces =
    sections.length >= 2 ||
    (loneSpace !== undefined &&
      (loneSpace.id !== "workspace" || (!dashboardsPending && !showDashboards)));
  const compactSpaces = showDashboards && sections.length >= 2;

  const shown = home.featured;
  const more = home.total - home.featured.length;
  const cards = shown.map((dashboard) => (
    <DashboardCard key={dashboard.id} dashboard={dashboard} pinned={pinnedIds.has(dashboard.id)} />
  ));
  const dashboardsSection = showDashboards ? (
    <HomeSection
      id="home-dashboards"
      title="Dashboards"
      badge={<CountBadge count={home.total} noun={home.total === 1 ? "dashboard" : "dashboards"} />}
      action={<SectionButton to={DASHBOARD_LIST_HREF}>All dashboards</SectionButton>}
    >
      <div
        className={cn(
          "grid gap-4",
          // Three across at most, the "View all" place counting as one.
          more === 0 ? balancedGridClass(shown.length, "cards") : "sm:grid-cols-2 lg:grid-cols-3",
        )}
      >
        {cards}
        {more > 0 ? <ViewAllTile to={DASHBOARD_LIST_HREF} total={home.total} more={more} /> : null}
      </div>
    </HomeSection>
  ) : null;

  const recentSection =
    showDashboards && home.recent.length > 0 ? (
      <HomeSection id="home-recent" title="Recently opened">
        <RecentLinks recent={home.recent} />
      </HomeSection>
    ) : null;

  const spacesSection = !showSpaces ? null : compactSpaces ? (
    <HomeSection id="home-spaces" title="Spaces">
      <SpaceLinks spaces={sections} />
    </HomeSection>
  ) : loneSpace ? (
    <HomeSection id="home-spaces" title="Your space">
      <SpacePanel
        to={loneSpace.href}
        icon={loneSpace.icon}
        title={loneSpace.label}
        description={loneSpace.description}
        note={
          loneSpace.id === "workspace" && noDashboards && !needsSetup ? (
            <>
              <p className="font-medium text-primary">No dashboards available yet</p>
              <p className="mt-0.5">Dashboards published to your account will appear here.</p>
            </>
          ) : undefined
        }
      />
    </HomeSection>
  ) : (
    <HomeSection id="home-spaces" title="Your spaces">
      <nav
        aria-label="Your spaces"
        className={cn("grid gap-3", balancedGridClass(sections.length, "spaces"))}
      >
        {sections.map(({ id, href, icon, label, description }) => (
          <SectionTile key={id} to={href} icon={icon} title={label} description={description} />
        ))}
      </nav>
    </HomeSection>
  );

  return (
    <AppLayout>
      <div className="flex min-h-[calc(100dvh-3.5rem)] flex-col bg-background">
        <div className="flex-1">
          {/* ── Hero ── */}
          <header className="relative isolate shrink-0 overflow-hidden border-b border-border bg-surface px-6 dark:border-b-2 dark:border-b-brand-cyan/60 py-10 sm:px-10 sm:py-12">
            <div className="relative mx-auto max-w-6xl">
              <Logo variant="full" tone="auto" size={46} />
              <h1 className="mt-8 text-2xl font-semibold tracking-tight text-primary sm:text-3xl">
                {username
                  ? needsSetup
                    ? `Welcome to Nexus, ${username}.`
                    : `Welcome back, ${username}.`
                  : "Welcome to Nexus."}
              </h1>
              <p className="mt-2 text-sm text-secondary">
                Your NAGRA® Anti-Piracy Center, powered by NEXUS.
              </p>
              {account.data && tenants.isSuccess ? (
                <HomeScope profile={profile} perimeter={perimeter} />
              ) : null}
            </div>
          </header>

          {/* ── Content ── */}
          <main
            id="main-content"
            tabIndex={-1}
            className="w-full flex-1 px-6 py-8 sm:px-10 sm:py-10"
          >
            <div className="mx-auto flex max-w-6xl flex-col gap-10">
              {needsSetup && account.data ? (
                <HomeAccessSetup
                  account={account.data}
                  checking={account.isFetching || tenants.isFetching}
                  onCheckAgain={() => {
                    void account.refetch();
                    void tenants.refetch();
                  }}
                />
              ) : (
                <>
                  {/* The dashboards lead (pinned ones first), then the spaces, then recents. */}
                  {dashboardsPending ? <HomeDashboardsSkeleton /> : dashboardsSection}
                  {spacesSection}
                  {recentSection}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </AppLayout>
  );
}
